/**
 * AchievementsManagement.js
 *
 * Página de gestión de logros y niveles
 * - Logros individuales y especiales en formato lista compacto
 * - Niveles por tipo de torneo con edición de umbrales
 */

import { useState, useEffect } from 'react';
import { Row, Col, Button, Tab, Tabs, Badge, Alert, Modal, Form } from 'react-bootstrap';
import { FaTrophy, FaPlus, FaEdit, FaTrash, FaStar, FaMedal, FaLayerGroup, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AdminCRUDLayout from '../../components/admin/AdminCRUDLayout';
import postgresAchievementService from '../../services/postgresAchievementService';
import './AchievementsManagement.css';

const AchievementsManagement = () => {
  // Estados principales
  const [logrosIndividuales, setLogrosIndividuales] = useState([]);
  const [logrosEspeciales, setLogrosEspeciales] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [tiposTorneo, setTiposTorneo] = useState([]);
  const [selectedTipoTorneo, setSelectedTipoTorneo] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('individuales');

  // Estados de modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Estados del formulario
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Estado para edición inline de logros
  const [editingLogro, setEditingLogro] = useState(null);
  const [logroFormData, setLogroFormData] = useState({});

  // Estados para edición de niveles
  const [editingNivel, setEditingNivel] = useState(null);
  const [nivelFormData, setNivelFormData] = useState({});
  const [showCreateNivelModal, setShowCreateNivelModal] = useState(false);
  const [nivelCreateData, setNivelCreateData] = useState({ nivel: '', descripcion: '', puntos_minimos: 0, puntos_maximos: null, color: '#808080', icono: '⭐', orden: 1 });

  // Mapeo de imágenes de logros
  const logroImages = {
    'asistencia': 'constante.jpeg', 'victoria': 'incansable.jpeg', 'MVP': 'eljugon.jpeg',
    'especial': 'desequilibrante.jpeg', 'el_jugon': 'eljugon.jpeg', 'jugador_campo': 'jugadorcampo.jpeg',
    'portero': 'portero.jpeg', 'lider': 'lider.jpeg', 'desequilibrante': 'desequilibrante.jpeg',
    'mayor_progresion': 'progresion.jpeg', 'constancia': 'constante.jpeg',
    'ganador_incansable': 'incansable.jpeg', 'mejor_companero': 'mejorcompanero.jpeg',
    'cerrojo': 'cerrojo.jpeg', 'progresion': 'progresion.jpeg',
    'incansable': 'incansable.jpeg', 'mejorcompanero': 'mejorcompanero.jpeg'
  };

  // Cargar datos
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [individuales, especiales, nivelesRes] = await Promise.all([
        postgresAchievementService.getLogrosIndividuales(),
        postgresAchievementService.getLogrosEspeciales(),
        postgresAchievementService.getNiveles()
      ]);

      setLogrosIndividuales(individuales.data || []);
      setLogrosEspeciales(especiales.data || []);

      const nivelesData = nivelesRes.data || [];
      setNiveles(nivelesData);
      // Extraer tipos de torneo únicos
      const tipos = [...new Set(nivelesData.map(n => n.tipo_torneo))].sort();
      setTiposTorneo(tipos.length > 0 ? tipos : ['normal']);
      if (tipos.length > 0 && !tipos.includes(selectedTipoTorneo)) {
        setSelectedTipoTorneo(tipos[0]);
      }
    } catch (err) {
      console.error('Error al cargar logros:', err);
      setError(err.response?.data?.message || 'Error al cargar los datos');
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener imagen responsive
  const getLogroImage = (nombre) => {
    // Buscar coincidencia exacta
    let imageName = logroImages[nombre];

    // Si no hay coincidencia exacta, buscar por palabras clave
    if (!imageName) {
      const nombreLower = nombre.toLowerCase();
      if (nombreLower.includes('progres')) imageName = 'progresion.jpeg';
      else if (nombreLower.includes('constant') || nombreLower.includes('asist')) imageName = 'constante.jpeg';
      else if (nombreLower.includes('incans') || nombreLower.includes('ganador')) imageName = 'incansable.jpeg';
      else if (nombreLower.includes('compañer') || nombreLower.includes('companero')) imageName = 'mejorcompanero.jpeg';
      else if (nombreLower.includes('cerroj') || nombreLower.includes('defens')) imageName = 'cerrojo.jpeg';
      else if (nombreLower.includes('lider')) imageName = 'lider.jpeg';
      else if (nombreLower.includes('victor')) imageName = 'incansable.jpeg';
      else if (nombreLower.includes('jugon') || nombreLower.includes('mvp')) imageName = 'eljugon.jpeg';
      else if (nombreLower.includes('desequilibr') || nombreLower.includes('especial')) imageName = 'desequilibrante.jpeg';
      else if (nombreLower.includes('portero')) imageName = 'portero.jpeg';
      else if (nombreLower.includes('campo')) imageName = 'jugadorcampo.jpeg';
      else imageName = 'eljugon.jpeg'; // Imagen por defecto
    }

    return {
      src1x: `/img/1x/${imageName}`,
      src4x: `/img/4x/${imageName}`
    };
  };

  // Handlers para CRUD
  const handleCreate = () => {
    setCurrentItem(null);
    setFormData({});
    setShowCreateModal(true);
  };

  const handleEdit = (item) => {
    setEditingLogro(item.id);
    setLogroFormData({
      nombre: item.nombre,
      descripcion: item.descripcion,
      puntos: item.puntos
    });
  };

  const handleDelete = (item) => {
    setCurrentItem(item);
    setShowDeleteModal(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      if (activeTab === 'individuales') {
        await postgresAchievementService.createLogroIndividual(formData);
        toast.success('Logro individual creado exitosamente');
      } else {
        await postgresAchievementService.createLogroEspecial(formData);
        toast.success('Logro especial creado exitosamente');
      }
      
      setShowCreateModal(false);
      await loadData();
    } catch (error) {
      console.error('Error al crear logro:', error);
      const errorMessage = error.response?.data?.message || 'Error al crear el logro';
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveLogro = async (logro) => {
    try {
      setFormLoading(true);
      if (activeTab === 'individuales') {
        await postgresAchievementService.updateLogroIndividual(logro.id, logroFormData);
        toast.success('Logro individual actualizado');
      } else {
        await postgresAchievementService.updateLogroEspecial(logro.id, logroFormData);
        toast.success('Logro especial actualizado');
      }
      setEditingLogro(null);
      await loadData();
    } catch (error) {
      console.error('Error al actualizar logro:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el logro');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmitDelete = async () => {
    setFormLoading(true);
    
    try {
      if (activeTab === 'individuales') {
        await postgresAchievementService.deleteLogroIndividual(currentItem.id);
        toast.success('Logro individual eliminado exitosamente');
      } else {
        await postgresAchievementService.deleteLogroEspecial(currentItem.id);
        toast.success('Logro especial eliminado exitosamente');
      }
      
      setShowDeleteModal(false);
      await loadData();
    } catch (error) {
      console.error('Error al eliminar logro:', error);
      const errorMessage = error.response?.data?.message || 'Error al eliminar el logro';
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  // Renderizar niveles por tipo de torneo
  const nivelesFiltrados = niveles.filter(n => n.tipo_torneo === selectedTipoTorneo).sort((a, b) => a.orden - b.orden);

  const handleSaveNivel = async (nivel) => {
    try {
      setFormLoading(true);
      await postgresAchievementService.updateNivel(nivel.id, nivelFormData);
      toast.success('Nivel actualizado');
      setEditingNivel(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al actualizar nivel');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateNivel = async (e) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      await postgresAchievementService.createNivel({ ...nivelCreateData, tipo_torneo: selectedTipoTorneo });
      toast.success('Nivel creado');
      setShowCreateNivelModal(false);
      setNivelCreateData({ nivel: '', descripcion: '', puntos_minimos: 0, puntos_maximos: null, color: '#808080', icono: '⭐', orden: 1 });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al crear nivel');
    } finally {
      setFormLoading(false);
    }
  };

  const currentData = activeTab === 'individuales' ? logrosIndividuales : activeTab === 'especiales' ? logrosEspeciales : [];

  return (
    <AdminCRUDLayout
      title="Gestión de Logros y Niveles"
      subtitle="Logros individuales, especiales y umbrales de niveles por tipo de torneo"
      loading={loading}
      error={error}
      onErrorDismiss={() => setError(null)}
    >
      <div className="admin-card" style={{ padding: 0 }}>
        <div style={{ borderBottom: '1px solid #dee2e6', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k); setEditingLogro(null); }} className="border-0" style={{ flex: 1 }}>
            <Tab eventKey="individuales" title={<span><FaMedal className="me-1" /> Individuales <Badge bg="primary" className="ms-1" style={{fontSize:'11px'}}>{logrosIndividuales.length}</Badge></span>} />
            <Tab eventKey="especiales" title={<span><FaStar className="me-1" /> Especiales <Badge bg="warning" className="ms-1" style={{fontSize:'11px'}}>{logrosEspeciales.length}</Badge></span>} />
            <Tab eventKey="niveles" title={<span><FaLayerGroup className="me-1" /> Niveles</span>} />
          </Tabs>
          {activeTab === 'niveles' ? (
            <button className="btn btn-success btn-sm" onClick={() => setShowCreateNivelModal(true)}><FaPlus className="me-1" /> Nuevo Nivel</button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={handleCreate}><FaPlus className="me-1" /> Nuevo Logro</button>
          )}
        </div>

        {activeTab === 'niveles' ? (
          /* ===== TAB NIVELES ===== */
          <div style={{ padding: '20px' }}>
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold" style={{fontSize:'14px'}}>Tipo de torneo:</span>
                <div className="d-flex gap-1">
                  {tiposTorneo.map(tipo => (
                    <Button key={tipo} size="sm" variant={selectedTipoTorneo === tipo ? 'primary' : 'outline-secondary'} onClick={() => setSelectedTipoTorneo(tipo)} style={{fontSize:'13px', textTransform:'capitalize'}}>
                      {tipo === 'normal' ? '🏆 Normal' : tipo === 'summer' ? '☀️ Summer' : `⚙️ ${tipo}`}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            {nivelesFiltrados.length === 0 ? (
              <div className="text-center py-4 text-muted"><FaLayerGroup size={32} className="mb-2" /><p>No hay niveles para <strong>{selectedTipoTorneo}</strong></p></div>
            ) : (
              <div className="table-responsive">
                <table className="data-table" style={{width:'100%'}}>
                  <thead>
                    <tr>
                      <th style={{width:'60px'}}>Orden</th>
                      <th style={{width:'60px'}}>Icono</th>
                      <th>Nivel</th>
                      <th>Descripción</th>
                      <th style={{width:'90px'}}>PP Mín</th>
                      <th style={{width:'90px'}}>PP Máx</th>
                      <th style={{width:'60px'}}>Color</th>
                      <th style={{width:'100px'}}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nivelesFiltrados.map(nivel => (
                      editingNivel === nivel.id ? (
                        <tr key={nivel.id} style={{backgroundColor:'#fffde7'}}>
                          <td><Form.Control size="sm" type="number" value={nivelFormData.orden ?? ''} onChange={e => setNivelFormData({...nivelFormData, orden: parseInt(e.target.value)})} style={{width:'55px'}} /></td>
                          <td><Form.Control size="sm" type="text" value={nivelFormData.icono ?? ''} onChange={e => setNivelFormData({...nivelFormData, icono: e.target.value})} style={{width:'55px'}} /></td>
                          <td><Form.Control size="sm" type="text" value={nivelFormData.nivel ?? ''} onChange={e => setNivelFormData({...nivelFormData, nivel: e.target.value})} /></td>
                          <td><Form.Control size="sm" type="text" value={nivelFormData.descripcion ?? ''} onChange={e => setNivelFormData({...nivelFormData, descripcion: e.target.value})} /></td>
                          <td><Form.Control size="sm" type="number" value={nivelFormData.puntos_minimos ?? ''} onChange={e => setNivelFormData({...nivelFormData, puntos_minimos: parseInt(e.target.value)})} style={{width:'75px'}} /></td>
                          <td><Form.Control size="sm" type="number" value={nivelFormData.puntos_maximos ?? ''} onChange={e => setNivelFormData({...nivelFormData, puntos_maximos: e.target.value === '' ? null : parseInt(e.target.value)})} placeholder="∞" style={{width:'75px'}} /></td>
                          <td><Form.Control size="sm" type="color" value={nivelFormData.color || '#808080'} onChange={e => setNivelFormData({...nivelFormData, color: e.target.value})} style={{width:'40px',height:'30px',padding:'2px'}} /></td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-success" onClick={() => handleSaveNivel(nivel)} disabled={formLoading}><FaSave /></button>
                              <button className="btn btn-sm btn-secondary" onClick={() => setEditingNivel(null)}>✕</button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={nivel.id}>
                          <td className="text-center">{nivel.orden}</td>
                          <td style={{fontSize:'20px'}}>{nivel.icono}</td>
                          <td className="fw-semibold">{nivel.nivel}</td>
                          <td className="text-muted">{nivel.descripcion || '—'}</td>
                          <td>{nivel.puntos_minimos}</td>
                          <td>{nivel.puntos_maximos != null ? nivel.puntos_maximos : '∞'}</td>
                          <td><div style={{width:'22px',height:'22px',borderRadius:'4px',backgroundColor:nivel.color,border:'1px solid #ddd'}}></div></td>
                          <td>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditingNivel(nivel.id); setNivelFormData({ nivel: nivel.nivel, descripcion: nivel.descripcion, puntos_minimos: nivel.puntos_minimos, puntos_maximos: nivel.puntos_maximos, color: nivel.color, icono: nivel.icono, orden: nivel.orden }); }}>
                              <FaEdit />
                            </button>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* ===== TAB LOGROS (individuales/especiales) ===== */
          <div>
            {currentData.length === 0 ? (
              <div className="text-center py-5"><FaTrophy className="text-muted mb-2" size={36} /><p className="text-muted">No hay logros registrados</p></div>
            ) : (
              <div className="table-responsive">
                <table className="data-table" style={{width:'100%'}}>
                  <thead>
                    <tr>
                      <th style={{width:'30px'}}></th>
                      <th style={{width:'50px'}}>Icono</th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th style={{width:'100px'}}>Puntos</th>
                      <th style={{width:'100px'}}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map(logro => {
                      const images = getLogroImage(logro.nombre);
                      return editingLogro === logro.id ? (
                        <tr key={logro.id} style={{backgroundColor:'#fffde7'}}>
                          <td></td>
                          <td><img src={images.src1x} alt={logro.nombre} style={{width:'36px',height:'36px',borderRadius:'8px',objectFit:'cover'}} /></td>
                          <td><Form.Control size="sm" type="text" value={logroFormData.nombre ?? ''} onChange={e => setLogroFormData({...logroFormData, nombre: e.target.value})} /></td>
                          <td><Form.Control size="sm" type="text" value={logroFormData.descripcion ?? ''} onChange={e => setLogroFormData({...logroFormData, descripcion: e.target.value})} /></td>
                          <td><Form.Control size="sm" type="number" value={logroFormData.puntos ?? ''} onChange={e => setLogroFormData({...logroFormData, puntos: parseInt(e.target.value)})} style={{width:'75px'}} /></td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-success" onClick={() => handleSaveLogro(logro)} disabled={formLoading}><FaSave /></button>
                              <button className="btn btn-sm btn-secondary" onClick={() => setEditingLogro(null)}>✕</button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={logro.id}>
                          <td></td>
                          <td><img src={images.src1x} alt={logro.nombre} style={{width:'36px',height:'36px',borderRadius:'8px',objectFit:'cover'}} /></td>
                          <td className="fw-semibold">{logro.nombre}</td>
                          <td className="text-muted" style={{fontSize:'13px'}}>{logro.descripcion ? (logro.descripcion.length > 60 ? logro.descripcion.substring(0,60)+'...' : logro.descripcion) : '—'}</td>
                          <td className="fw-semibold">{logro.puntos != null ? `${logro.puntos} pts` : '—'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(logro)}><FaEdit /></button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(logro)}><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Crear Logro */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{fontSize:'16px'}}>Crear Logro {activeTab === 'individuales' ? 'Individual' : 'Especial'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmitCreate}>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label className="small fw-bold">Nombre</Form.Label><Form.Control size="sm" type="text" value={formData.nombre || ''} onChange={e => setFormData({...formData, nombre: e.target.value})} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="small fw-bold">Descripción</Form.Label><Form.Control size="sm" as="textarea" rows={2} value={formData.descripcion || ''} onChange={e => setFormData({...formData, descripcion: e.target.value})} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="small fw-bold">Puntos</Form.Label><Form.Control size="sm" type="number" min="0" value={formData.puntos || ''} onChange={e => setFormData({...formData, puntos: parseInt(e.target.value)})} required /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button size="sm" variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button><Button size="sm" variant="primary" type="submit" disabled={formLoading}>{formLoading ? 'Creando...' : 'Crear'}</Button></Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Eliminar Logro */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{fontSize:'16px'}}>Confirmar Eliminación</Modal.Title></Modal.Header>
        <Modal.Body>
          {currentItem && (
            <div className="text-center">
              <FaTrash className="text-danger mb-2" size={32} />
              <p>¿Eliminar <strong>{currentItem.nombre}</strong>?</p>
              <Alert variant="warning" className="small py-2"><strong>Advertencia:</strong> Si está en uso, no se podrá eliminar.</Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button size="sm" variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button><Button size="sm" variant="danger" onClick={handleSubmitDelete} disabled={formLoading}>{formLoading ? 'Eliminando...' : 'Eliminar'}</Button></Modal.Footer>
      </Modal>

      {/* Modal Crear Nivel */}
      <Modal show={showCreateNivelModal} onHide={() => setShowCreateNivelModal(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{fontSize:'16px'}}>Crear Nivel para &ldquo;{selectedTipoTorneo}&rdquo;</Modal.Title></Modal.Header>
        <Form onSubmit={handleCreateNivel}>
          <Modal.Body>
            <Row>
              <Col xs={8}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Nombre del nivel</Form.Label><Form.Control size="sm" type="text" value={nivelCreateData.nivel} onChange={e => setNivelCreateData({...nivelCreateData, nivel: e.target.value})} required placeholder="Ej: Diamante" /></Form.Group></Col>
              <Col xs={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Orden</Form.Label><Form.Control size="sm" type="number" value={nivelCreateData.orden} onChange={e => setNivelCreateData({...nivelCreateData, orden: parseInt(e.target.value)})} required /></Form.Group></Col>
            </Row>
            <Form.Group className="mb-2"><Form.Label className="small fw-bold">Descripción</Form.Label><Form.Control size="sm" type="text" value={nivelCreateData.descripcion} onChange={e => setNivelCreateData({...nivelCreateData, descripcion: e.target.value})} /></Form.Group>
            <Row>
              <Col xs={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">PP Mín</Form.Label><Form.Control size="sm" type="number" value={nivelCreateData.puntos_minimos} onChange={e => setNivelCreateData({...nivelCreateData, puntos_minimos: parseInt(e.target.value)})} required /></Form.Group></Col>
              <Col xs={4}><Form.Group className="mb-2"><Form.Label className="small fw-bold">PP Máx</Form.Label><Form.Control size="sm" type="number" value={nivelCreateData.puntos_maximos ?? ''} onChange={e => setNivelCreateData({...nivelCreateData, puntos_maximos: e.target.value === '' ? null : parseInt(e.target.value)})} placeholder="∞" /></Form.Group></Col>
              <Col xs={2}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Icono</Form.Label><Form.Control size="sm" type="text" value={nivelCreateData.icono} onChange={e => setNivelCreateData({...nivelCreateData, icono: e.target.value})} /></Form.Group></Col>
              <Col xs={2}><Form.Group className="mb-2"><Form.Label className="small fw-bold">Color</Form.Label><Form.Control size="sm" type="color" value={nivelCreateData.color} onChange={e => setNivelCreateData({...nivelCreateData, color: e.target.value})} style={{height:'31px',padding:'1px'}} /></Form.Group></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer><Button size="sm" variant="secondary" onClick={() => setShowCreateNivelModal(false)}>Cancelar</Button><Button size="sm" variant="success" type="submit" disabled={formLoading}>{formLoading ? 'Creando...' : 'Crear Nivel'}</Button></Modal.Footer>
        </Form>
      </Modal>
    </AdminCRUDLayout>
  );
};

export default AchievementsManagement;
